import React, { useState, useEffect } from 'react';
import Layout from '../components/common/Layout';
import {
    Tag,
    Plus,
    Search,
    Eye,
    Edit,
    Trash2,
    ToggleLeft,
    ToggleRight,
    Package,
    CheckCircle,
    XCircle,
    AlertCircle
} from 'lucide-react';
import Button from '../components/common/Button';
import Modal from '../components/common/Modal';
import Loading from '../components/common/Loading';
import { categoriasService } from '../services/categorias';
import toast from 'react-hot-toast';

const CategoriasPage = () => {
    const [categorias, setCategorias] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [showModal, setShowModal] = useState(false);
    const [modalType, setModalType] = useState('create');
    const [selectedCategoria, setSelectedCategoria] = useState(null);
    const [formData, setFormData] = useState({
        nombre: '',
        descripcion: '',
        activa: true
    });

    // Cargar categorías al montar el componente
    useEffect(() => {
        loadCategorias();
    }, []);

    // Cargar categorías desde la API
    const loadCategorias = async (search = '') => {
        try {
            setLoading(true);
            const data = await categoriasService.getAll({ search });
            setCategorias(data);
        } catch (error) {
            console.error('Error al cargar categorías:', error);
            toast.error('Error al cargar categorías');
        } finally {
            setLoading(false);
        }
    };

    // Buscar categorías
    const handleSearch = async () => {
        await loadCategorias(searchTerm);
    };

    // Búsqueda con delay
    useEffect(() => {
        const delayedSearch = setTimeout(() => {
            if (searchTerm !== '') {
                handleSearch();
            } else {
                loadCategorias();
            }
        }, 500);

        return () => clearTimeout(delayedSearch);
    }, [searchTerm]);

    // Crear categoría
    const handleCreate = () => {
        setModalType('create');
        setFormData({
            nombre: '',
            descripcion: '',
            activa: true
        });
        setShowModal(true);
    };

    // Editar categoría
    const handleEdit = (categoria) => {
        setModalType('edit');
        setSelectedCategoria(categoria);
        setFormData({
            nombre: categoria.nombre,
            descripcion: categoria.descripcion || '',
            activa: categoria.activa
        });
        setShowModal(true);
    };

    // Ver detalles
    const handleView = async (categoria) => {
        try {
            const stats = await categoriasService.getStats(categoria.id);
            setModalType('view');
            setSelectedCategoria({ ...categoria, stats: stats });
            setShowModal(true);
        } catch (error) {
            console.error('Error al cargar estadísticas:', error);
            toast.error('Error al cargar detalles de la categoría');
        }
    };

    // Eliminar categoría
    const handleDelete = async (categoria) => {
        if (window.confirm(`¿Estás seguro de eliminar la categoría "${categoria.nombre}"?`)) {
            try {
                await categoriasService.delete(categoria.id);
                toast.success('Categoría eliminada correctamente');
                loadCategorias();
            } catch (error) {
                console.error('Error al eliminar categoría:', error);
                const errorMessage = error.response?.data?.detail || 'Error al eliminar categoría';
                toast.error(errorMessage);
            }
        }
    };

    // Cambiar estado activa/inactiva
    const handleToggleActiva = async (categoria) => {
        try {
            await categoriasService.toggleActiva(categoria.id);
            const action = categoria.activa ? 'desactivada' : 'activada';
            toast.success(`Categoría ${action} correctamente`);
            loadCategorias();
        } catch (error) {
            console.error('Error al cambiar estado:', error);
            toast.error('Error al cambiar estado de la categoría');
        }
    };

    // Guardar categoría (crear o editar)
    const handleSubmit = async (e) => {
        e.preventDefault();

        try {
            // Validaciones básicas
            if (!formData.nombre.trim()) {
                toast.error('El nombre es obligatorio');
                return;
            }

            if (modalType === 'create') {
                await categoriasService.create(formData);
                toast.success('Categoría creada correctamente');
            } else if (modalType === 'edit') {
                await categoriasService.update(selectedCategoria.id, formData);
                toast.success('Categoría actualizada correctamente');
            }

            setShowModal(false);
            loadCategorias();
        } catch (error) {
            console.error('Error al guardar categoría:', error);
            const errorMessage = error.response?.data?.detail || 'Error al guardar categoría';
            toast.error(errorMessage);
        }
    };

    const getStatusBadge = (categoria) => {
        if (categoria.activa) {
            return (
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                    <CheckCircle className="w-3 h-3 mr-1" />
                    Activa
                </span>
            );
        }
        return (
            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                <XCircle className="w-3 h-3 mr-1" />
                Inactiva
            </span>
        );
    };

    if (loading) {
        return (
            <Layout>
                <Loading message="Cargando categorías..." />
            </Layout>
        );
    }

    return (
        <Layout>
            <div className="p-6">
                {/* Header */}
                <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-8">
                    <div className="flex items-center space-x-3 mb-4 md:mb-0">
                        <Tag className="h-8 w-8 text-blue-600" />
                        <div>
                            <h1 className="text-3xl font-bold text-gray-900">Gestión de Categorías</h1>
                            <p className="text-gray-600">Administra las categorías de activos</p>
                        </div>
                    </div>

                    <div className="flex space-x-3">
                        <Button onClick={handleCreate} className="flex items-center space-x-2">
                            <Plus className="h-4 w-4" />
                            <span>Nueva Categoría</span>
                        </Button>
                    </div>
                </div>

                {/* Filters */}
                <div className="bg-white rounded-lg shadow-md p-6 mb-6">
                    <div className="flex flex-col md:flex-row space-y-4 md:space-y-0 md:space-x-4">
                        <div className="flex-1 relative">
                            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                            <input
                                type="text"
                                placeholder="Buscar categorías por nombre o descripción..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                            />
                        </div>
                    </div>
                </div>

                {/* Stats Cards */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
                    <div className="bg-white rounded-lg shadow-md p-6">
                        <div className="flex items-center">
                            <Tag className="h-8 w-8 text-blue-600" />
                            <div className="ml-4">
                                <p className="text-sm font-medium text-gray-600">Total Categorías</p>
                                <p className="text-2xl font-bold text-gray-900">{categorias.length}</p>
                            </div>
                        </div>
                    </div>

                    <div className="bg-white rounded-lg shadow-md p-6">
                        <div className="flex items-center">
                            <CheckCircle className="h-8 w-8 text-green-600" />
                            <div className="ml-4">
                                <p className="text-sm font-medium text-gray-600">Activas</p>
                                <p className="text-2xl font-bold text-gray-900">
                                    {categorias.filter(c => c.activa).length}
                                </p>
                            </div>
                        </div>
                    </div>

                    <div className="bg-white rounded-lg shadow-md p-6">
                        <div className="flex items-center">
                            <XCircle className="h-8 w-8 text-red-600" />
                            <div className="ml-4">
                                <p className="text-sm font-medium text-gray-600">Inactivas</p>
                                <p className="text-2xl font-bold text-gray-900">
                                    {categorias.filter(c => !c.activa).length}
                                </p>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Categorías Table */}
                <div className="bg-white rounded-lg shadow-md overflow-hidden">
                    <div className="px-6 py-4 border-b border-gray-200">
                        <h2 className="text-lg font-semibold text-gray-900">
                            Lista de Categorías ({categorias.length})
                        </h2>
                    </div>

                    <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-gray-50">
                                <tr>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Nombre
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Descripción
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Estado
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Fecha Creación
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Acciones
                                    </th>
                                </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                                {categorias.map((categoria) => (
                                    <tr key={categoria.id} className="hover:bg-gray-50">
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <div className="flex items-center">
                                                <Tag className="h-5 w-5 text-gray-400 mr-2" />
                                                <span className="text-sm font-medium text-gray-900">
                                                    {categoria.nombre}
                                                </span>
                                            </div>
                                        </td>
                                        <td className="px-6 py-4">
                                            <div className="text-sm text-gray-900">
                                                {categoria.descripcion || 'Sin descripción'}
                                            </div>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            {getStatusBadge(categoria)}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <div className="text-sm text-gray-900">
                                                {new Date(categoria.created_at).toLocaleDateString('es-ES')}
                                            </div>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                                            <div className="flex items-center space-x-2">
                                                <button
                                                    onClick={() => handleView(categoria)}
                                                    className="text-blue-600 hover:text-blue-900"
                                                    title="Ver detalles"
                                                >
                                                    <Eye className="h-4 w-4" />
                                                </button>
                                                <button
                                                    onClick={() => handleEdit(categoria)}
                                                    className="text-green-600 hover:text-green-900"
                                                    title="Editar"
                                                >
                                                    <Edit className="h-4 w-4" />
                                                </button>
                                                <button
                                                    onClick={() => handleToggleActiva(categoria)}
                                                    className={`${categoria.activa ? 'text-orange-600 hover:text-orange-900' : 'text-green-600 hover:text-green-900'}`}
                                                    title={categoria.activa ? 'Desactivar' : 'Activar'}
                                                >
                                                    {categoria.activa ? <ToggleRight className="h-4 w-4" /> : <ToggleLeft className="h-4 w-4" />}
                                                </button>
                                                <button
                                                    onClick={() => handleDelete(categoria)}
                                                    className="text-red-600 hover:text-red-900"
                                                    title="Eliminar"
                                                >
                                                    <Trash2 className="h-4 w-4" />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>

                        {categorias.length === 0 && (
                            <div className="text-center py-8">
                                <Tag className="mx-auto h-12 w-12 text-gray-400" />
                                <h3 className="mt-2 text-sm font-medium text-gray-900">No hay categorías</h3>
                                <p className="mt-1 text-sm text-gray-500">
                                    {searchTerm ? 'No se encontraron categorías con esa búsqueda' : 'Comienza creando una nueva categoría'}
                                </p>
                            </div>
                        )}
                    </div>
                </div>

                {/* Modal para Crear/Editar/Ver Categoría */}
                {showModal && (
                    <Modal
                        title={
                            modalType === 'create' ? 'Nueva Categoría' :
                                modalType === 'edit' ? 'Editar Categoría' :
                                    'Detalles de la Categoría'
                        }
                        onClose={() => setShowModal(false)}
                        size="md"
                    >
                        {modalType === 'view' ? (
                            <CategoriaDetails categoria={selectedCategoria} />
                        ) : (
                            <CategoriaForm
                                formData={formData}
                                setFormData={setFormData}
                                onSubmit={handleSubmit}
                                isEdit={modalType === 'edit'}
                                onClose={() => setShowModal(false)}
                            />
                        )}
                    </Modal>
                )}
            </div>
        </Layout>
    );
};

// Componente para el formulario
const CategoriaForm = ({ formData, setFormData, onSubmit, isEdit, onClose }) => {
    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
    };

    return (
        <form onSubmit={onSubmit} className="space-y-6">
            <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                    Nombre <span className="text-red-500">*</span>
                </label>
                <input
                    type="text"
                    name="nombre"
                    value={formData.nombre}
                    onChange={handleChange}
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Ej: Equipos de Producción"
                />
            </div>

            <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                    Descripción
                </label>
                <textarea
                    name="descripcion"
                    value={formData.descripcion}
                    onChange={handleChange}
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Descripción opcional de la categoría..."
                />
            </div>

            <div className="flex items-center">
                <input
                    type="checkbox"
                    name="activa"
                    checked={formData.activa}
                    onChange={handleChange}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                />
                <label className="ml-2 block text-sm text-gray-900">
                    Categoría activa
                </label>
            </div>

            <div className="flex justify-end space-x-3 pt-6 border-t">
                <Button type="button" variant="outline" onClick={onClose}>
                    Cancelar
                </Button>
                <Button type="submit">
                    {isEdit ? 'Actualizar' : 'Crear'} Categoría
                </Button>
            </div>
        </form>
    );
};

// Componente para mostrar detalles
const CategoriaDetails = ({ categoria }) => {
    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        return new Date(dateString).toLocaleDateString('es-ES', {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    return (
        <div className="space-y-6">
            <div className="grid grid-cols-1 gap-6">
                <div>
                    <h3 className="text-lg font-medium text-gray-900 mb-4">Información General</h3>
                    <dl className="space-y-3">
                        <div>
                            <dt className="text-sm font-medium text-gray-500">Nombre</dt>
                            <dd className="text-sm text-gray-900 flex items-center">
                                <Tag className="h-4 w-4 mr-1 text-gray-400" />
                                {categoria.nombre}
                            </dd>
                        </div>
                        <div>
                            <dt className="text-sm font-medium text-gray-500">Descripción</dt>
                            <dd className="text-sm text-gray-900">{categoria.descripcion || 'Sin descripción'}</dd>
                        </div>
                        <div>
                            <dt className="text-sm font-medium text-gray-500">Estado</dt>
                            <dd className="text-sm text-gray-900">
                                {categoria.activa ? (
                                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                        <CheckCircle className="w-3 h-3 mr-1" />
                                        Activa
                                    </span>
                                ) : (
                                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                                        <XCircle className="w-3 h-3 mr-1" />
                                        Inactiva
                                    </span>
                                )}
                            </dd>
                        </div>
                        <div>
                            <dt className="text-sm font-medium text-gray-500">Fecha de Creación</dt>
                            <dd className="text-sm text-gray-900">
                                {formatDate(categoria.created_at)}
                            </dd>
                        </div>
                        {categoria.updated_at && (
                            <div>
                                <dt className="text-sm font-medium text-gray-500">Última Actualización</dt>
                                <dd className="text-sm text-gray-900">
                                    {formatDate(categoria.updated_at)}
                                </dd>
                            </div>
                        )}
                    </dl>
                </div>

                {categoria.stats && (
                    <div>
                        <h3 className="text-lg font-medium text-gray-900 mb-4">Estadísticas</h3>
                        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                            <div className="flex items-center">
                                <Package className="h-8 w-8 text-blue-600" />
                                <div className="ml-4">
                                    <p className="text-sm font-medium text-blue-900">Activos Asociados</p>
                                    <p className="text-2xl font-bold text-blue-900">{categoria.stats.total_activos}</p>
                                </div>
                            </div>
                        </div>
                        
                        {categoria.stats.total_activos > 0 && (
                            <div className="mt-4 bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                                <div className="flex items-start">
                                    <AlertCircle className="h-5 w-5 text-yellow-600 mt-0.5" />
                                    <div className="ml-2">
                                        <p className="text-sm font-medium text-yellow-800">Información importante</p>
                                        <p className="text-sm text-yellow-700">
                                            Esta categoría tiene {categoria.stats.total_activos} activos asociados. 
                                            No podrás eliminarla hasta que reasignes estos activos a otras categorías.
                                        </p>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};

export default CategoriasPage;